import _ from 'lodash';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAsyncEffect } from 'rooks';
import { getSupportedUnits } from '@/services/systemConfiguration.utilities';
import { Select } from '@seeqdev/qomponents';
import type { StyleXStyles } from '@stylexjs/stylex';
import type { FilterOptionOption, MultiValue, SingleValue } from 'react-select';

type UnitsByCategory = Record<string, string[]>;

interface UnitOption {
  searchString: string;
  value: string;
  label: ReactNode;
}

interface UnitOptionGroup {
  label: string;
  options: UnitOption[];
}

interface SelectFilterConfig<OptionType> {
  ignoreCase?: boolean;
  ignoreAccents?: boolean;
  trim?: boolean;
  matchFrom?: 'any' | 'start';
  stringify?: (option: FilterOptionOption<OptionType>) => string;
}

/**
 * Select input to choose from a list of units of measure
 */
interface SelectUnitProps {
  onChange: (unit: string | undefined) => void;
  value?: string;
  height?: string;
  menuIsOpen?: boolean;
  isDisabled?: boolean;
  isClearable?: boolean;
  placeholder?: string;
  extraClassNames?: string;
  xstyle?: StyleXStyles;
  menuPortalTarget?: null | HTMLElement;
  units?: UnitsByCategory;
  showError?: boolean;
}

export const SelectUnit = (props: SelectUnitProps) => {
  const { t } = useTranslation();
  const {
    onChange,
    value,
    menuIsOpen,
    isDisabled = false,
    isClearable = true,
    placeholder,
    extraClassNames,
    xstyle,
    menuPortalTarget,
    showError,
    units,
  } = props;

  const [options, setOptions] = useState<UnitOptionGroup[]>([]);

  const formatCategory = (category: string) => _.chain(category).upperCase().replace(new RegExp(' ', 'g'), '_').value();

  useAsyncEffect(async () => {
    const unitsToUse: UnitsByCategory | undefined = _.isEmpty(units) ? await getSupportedUnits() : units;

    if (!unitsToUse) {
      setOptions([]);
      return;
    }

    const groupedOptions: UnitOptionGroup[] = Object.entries(unitsToUse).map(([category, unitsForCategory]) => ({
      label: t(`UNIT_CATEGORIES.${formatCategory(category)}`),
      options: unitsForCategory.map((unit) => ({
        searchString: `${t(`UNITS_BY_CATEGORY.${formatCategory(category)}.${unit}`)} ${t(
          `UNITS_BY_CATEGORY.${category}.${unit}`,
        )} ${unit}`,
        value: unit,
        label: (
          // be sure to also update the search string if you make changes here!
          <>
            <strong className="tw:pr-sq-5" data-testid="optionText">
              {unit}
            </strong>
            <small>{t(`UNITS_BY_CATEGORY.${formatCategory(category)}.${unit}`)}</small>
          </>
        ),
      })),
    }));

    setOptions(groupedOptions);
  }, []);

  const selectedValue: UnitOption | undefined = value
    ? (_.chain(options).flatMap('options').find({ value }).value() as UnitOption | undefined)
    : undefined;

  const filterConfig: SelectFilterConfig<UnitOption> = {
    ignoreCase: true,
    ignoreAccents: true,
    trim: false,
    matchFrom: 'any',
    stringify: (option) => option.data.searchString,
  };

  const isMultiValue = (data: SingleValue<UnitOption> | MultiValue<UnitOption>): data is MultiValue<UnitOption> =>
    Array.isArray(data);

  const handleChange = (data: SingleValue<UnitOption> | MultiValue<UnitOption>) => {
    if (isMultiValue(data)) {
      return;
    }

    onChange(data?.value);
  };

  return (
    <div data-testid="selectUnit">
      <Select
        placeholder={placeholder ? t(placeholder) : undefined}
        isClearable={isClearable}
        options={options}
        isSearchable={true}
        onChange={handleChange}
        value={selectedValue}
        menuIsOpen={menuIsOpen}
        menuPortalTarget={menuPortalTarget}
        isDisabled={isDisabled}
        filterConfig={filterConfig}
        extraClassNames={extraClassNames}
        xstyle={xstyle}
        showError={showError}
      />
    </div>
  );
};

export default SelectUnit;
